'use strict';

import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { withTranslation } from 'react-i18next';

import {
  PRESET_PREVIEW,
  privateShowStatus as privateShowConstants,
} from '../resource/liveStreamConstants.js';
import { ButtonId } from '../resource/mixpanel.js';
import { getIsIphoneXSeriesPWA } from '../resource/getUserAgent.js';
import { NULL_FUNCTION } from '../resource/defaults.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';

import UniversalComponent from '../component/UniversalComponent.jsx';
import Tooltip from '../component/Tooltip.jsx';

import ResetButtonStyle from '../style/ResetButtonStyle.js';
import {
  headerHeight,
  iPhoneXSeriesFooterPaddingInPWA,
  color,
} from '../style/variables.js';
import media from '../style/media.js';

import GameIconSrc from '../../img/ic-game-white-full.svg';
import GiftIconSrc from '../../img/icon-gift.png';
import ToyCommandIconSrc from '../../img/icon-toy-command.png';
import PrivateCtaIconSrc from '../../img/icon-private-cta.png';
import SendIconSrc from '../../img/ic-send.svg';

const isIphoneXSeriesPWA = getIsIphoneXSeriesPWA();

export const LivestreamDetailOnlineFooter = ({
  streamId = null,
  streamStatus = null,
  streamPreviewBlurDuration = 30,
  isAuthed = false,
  isStreamActivated = false,
  isLiveStreaming = null,
  isStreamer = false,
  isChatPanelOpen = false,
  isGamePanelOpen = false,
  isTriggerPrivatePanelOpen = false,
  isSuperChatOpen = false,
  hasStreamToken = false,
  hasGames = false,
  shouldDisplayBlurCountdown = false,
  shouldUseLivestreamAlert = false,
  toyCommandHintOpenedTimestamp = 0,
  privateShowStatus = null,
  isKaraokeDeviceOnline = false,
  login = NULL_FUNCTION,
  setChatPanel = NULL_FUNCTION,
  setGamePanel = NULL_FUNCTION,
  setTriggerPrivatePanel = NULL_FUNCTION,
  setGiftPanel = NULL_FUNCTION,
  openModal = NULL_FUNCTION,
  toggleModal = NULL_FUNCTION,
  checkLivestreamChatPreflightAndOpenPanel = NULL_FUNCTION,
  setToyCommandHintOpenedTimestamp = NULL_FUNCTION,
  t = NULL_FUNCTION,
}) => {
  const shouldDisplayChatButton =
    isAuthed &&
    isLiveStreaming &&
    (streamStatus === PRESET_PREVIEW || hasStreamToken);

  // Tooltip auto-display logic
  const shouldShowToyCommandHint = !toyCommandHintOpenedTimestamp;

  // Handle tooltip closed event
  const handleTooltipClosed = useCallback(() => {
    if (shouldShowToyCommandHint) {
      setToyCommandHintOpenedTimestamp({
        toyCommandHintOpenedTimestamp: Date.now(),
      });
    }
  }, [shouldShowToyCommandHint, setToyCommandHintOpenedTimestamp]);

  // Event handlers
  const toggleChatPanel = useCallback(() => {
    if (!isAuthed) {
      login();
    } else if (isStreamer) {
      setChatPanel({ isChatPanelOpen: !isChatPanelOpen });
    } else if (isChatPanelOpen) {
      setChatPanel({ isChatPanelOpen: false });
    } else {
      checkLivestreamChatPreflightAndOpenPanel({ streamId });
    }
  }, [
    isAuthed,
    isChatPanelOpen,
    isStreamer,
    login,
    streamId,
    setChatPanel,
    checkLivestreamChatPreflightAndOpenPanel,
  ]);

  const toggleGamePanel = useCallback(() => {
    setGamePanel({ isGamePanelOpen: !isGamePanelOpen });
  }, [isGamePanelOpen, setGamePanel]);

  const openToyCommandModal = useCallback(() => {
    toggleModal({
      modalId: 'LiveStreamKaraokeModal',
      payload: {
        mountElementId: isStreamActivated ? 'toy-command-button-mobile' : '',
        streamId,
      },
      isOpen: true,
    });
  }, [toggleModal, streamId, isStreamActivated]);

  const toggleTriggerPrivatePanel = useCallback(() => {
    setTriggerPrivatePanel({
      isTriggerPrivatePanelOpen: !isTriggerPrivatePanelOpen,
    });
  }, [isTriggerPrivatePanelOpen, setTriggerPrivatePanel]);

  const openLiveStreamGiftModal = useCallback(() => {
    setGiftPanel({ isGiftPanelOpen: true });
    openModal({ id: 'LiveStreamGiftModal' });
  }, [openModal, setGiftPanel]);

  const renderChatInput = useCallback(() => {
    const handleClick = () => {
      if (!shouldDisplayChatButton) return;
      if (!isAuthed && streamStatus === PRESET_PREVIEW) {
        login();
      } else {
        toggleChatPanel();
      }
    };

    return (
      <ChatInputDisplay
        $isHidden={isChatPanelOpen}
        $shouldDisplay={shouldDisplayChatButton}
        onClick={handleClick}
      >
        <ChatInputText>
          {t(
            shouldUseLivestreamAlert
              ? 'livestream_chat_notify_input'
              : 'chat_message_placeholder_say_hello',
            { ns: TranslationNamespace.LIVESTREAM }
          )}
        </ChatInputText>
        <ChatSendIcon>
          <img src={SendIconSrc} alt="Send" />
        </ChatSendIcon>
      </ChatInputDisplay>
    );
  }, [
    shouldDisplayChatButton,
    isChatPanelOpen,
    isAuthed,
    streamStatus,
    login,
    toggleChatPanel,
    shouldUseLivestreamAlert,
    t,
  ]);

  const renderGameButton = useCallback(() => {
    if (!hasGames) return null;

    return (
      <MobileAndTabletGameButton
        $isActive={isGamePanelOpen}
        data-element_id={ButtonId.LiveStream.ButtonGame}
        onClick={toggleGamePanel}
      >
        <img src={GameIconSrc} alt="Game" />
      </MobileAndTabletGameButton>
    );
  }, [hasGames, isGamePanelOpen, toggleGamePanel]);

  const renderGiftButton = useCallback(
    () => (
      <MobileAndTabletGiftButton
        data-element_id={ButtonId.All.ButtonGift}
        onClick={openLiveStreamGiftModal}
      >
        <img src={GiftIconSrc} alt="Gift" />
      </MobileAndTabletGiftButton>
    ),
    [openLiveStreamGiftModal]
  );

  const renderPrivateCtaButton = useCallback(() => {
    if (!(streamStatus === PRESET_PREVIEW || hasStreamToken)) return null;

    return (
      <MobileAndTabletPrivateCtaButton
        $isActive={isTriggerPrivatePanelOpen}
        data-element_id={ButtonId.LiveStream.ButtonUserTrigger}
        onClick={toggleTriggerPrivatePanel}
      >
        <img src={PrivateCtaIconSrc} alt="Private CTA" />
      </MobileAndTabletPrivateCtaButton>
    );
  }, [
    streamStatus,
    hasStreamToken,
    isTriggerPrivatePanelOpen,
    toggleTriggerPrivatePanel,
  ]);

  const renderTooltipHint = useCallback(() => {
    if (shouldShowToyCommandHint) {
      return (
        <ToyCommandTooltipWrapper>
          {t('tips_livestream_update_toy_commands', {
            ns: TranslationNamespace.LIVESTREAM,
          })}
        </ToyCommandTooltipWrapper>
      );
    }

    return null;
  }, [shouldShowToyCommandHint, t]);

  const renderToyCommandButton = useCallback(() => {
    const shouldStopKaraokeIfPerforming = [
      privateShowConstants.PERFORMING,
      privateShowConstants.PREPARING,
    ].includes(privateShowStatus);

    const shouldHideToyButton =
      shouldStopKaraokeIfPerforming && !isKaraokeDeviceOnline;

    return (
      <ToyCommandButtonWrapper $shouldHide={shouldHideToyButton}>
        <Tooltip
          key={shouldShowToyCommandHint ? 'show-tooltip' : 'hide-tooltip'}
          position="top-right"
          trigger="auto"
          renderHint={renderTooltipHint}
          onHintClosed={handleTooltipClosed}
        >
          <MobileAndTabletToyCommandButton
            data-element_id={ButtonId.LiveStream.ButtonCommandList}
            data-sheet_mount_id={
              isStreamActivated ? 'toy-command-button-mobile' : ''
            }
            onClick={openToyCommandModal}
          >
            <ToyCommandIconContainer>
              <img src={ToyCommandIconSrc} alt="Toy Command" />
            </ToyCommandIconContainer>
          </MobileAndTabletToyCommandButton>
        </Tooltip>
      </ToyCommandButtonWrapper>
    );
  }, [
    shouldShowToyCommandHint,
    renderTooltipHint,
    openToyCommandModal,
    handleTooltipClosed,
    privateShowStatus,
    isKaraokeDeviceOnline,
    isStreamActivated,
  ]);

  const ControlPanelComponent = useMemo(
    () => (
      <ControlPanel>
        {shouldDisplayBlurCountdown && isStreamActivated && (
          <UniversalComponent
            filename="Component_LivestreamPreviewBlurCountdown"
            importer={
              /* #__LOADABLE__ */ () =>
                import('../component/LivestreamPreviewBlurCountdown.jsx')
            }
            streamPreviewBlurDuration={streamPreviewBlurDuration}
          />
        )}
        {renderChatInput()}
        {renderGameButton()}
        {renderGiftButton()}
        {renderPrivateCtaButton()}
        {renderToyCommandButton()}
      </ControlPanel>
    ),
    [
      shouldDisplayBlurCountdown,
      streamPreviewBlurDuration,
      isStreamActivated,
      renderChatInput,
      renderGameButton,
      renderGiftButton,
      renderPrivateCtaButton,
      renderToyCommandButton,
    ]
  );
  const ChatPanelComponent = useMemo(() => {
    return (
      <MobileAndTabletChatPanelContainer className="swiper-no-swiping">
        <ChatBackDrop onClick={toggleChatPanel} />
        {isSuperChatOpen && (
          <SuperChatPanel>
            <UniversalComponent
              filename="Container_LivestreamSuperChatPanel"
              streamId={streamId}
              importer={
                /* #__LOADABLE__ */ () =>
                  import('../container/LivestreamSuperChatPanel.js')
              }
            />
          </SuperChatPanel>
        )}
        <ChatPanel>
          <ChatPanelInputWrapper>
            <UniversalComponent
              filename="Container_LiveStreamChatInput"
              streamId={streamId}
              importer={
                /* #__LOADABLE__ */ () =>
                  import('../container/LiveStreamChatInput.js')
              }
            />
          </ChatPanelInputWrapper>
        </ChatPanel>
      </MobileAndTabletChatPanelContainer>
    );
  }, [isSuperChatOpen, streamId, toggleChatPanel]);
  return (
    <StyledLivestreamDetailOnlineFooter>
      {!isChatPanelOpen && ControlPanelComponent}
      {isChatPanelOpen && isStreamActivated && ChatPanelComponent}
    </StyledLivestreamDetailOnlineFooter>
  );
};

LivestreamDetailOnlineFooter.propTypes = {
  streamId: PropTypes.string,
  streamStatus: PropTypes.string,
  streamPreviewBlurDuration: PropTypes.number,
  isAuthed: PropTypes.bool,
  isStreamActivated: PropTypes.bool,
  isLiveStreaming: PropTypes.bool,
  isStreamer: PropTypes.bool,
  isChatPanelOpen: PropTypes.bool,
  isGamePanelOpen: PropTypes.bool,
  isTriggerPrivatePanelOpen: PropTypes.bool,
  isSuperChatOpen: PropTypes.bool,
  hasStreamToken: PropTypes.bool,
  hasGames: PropTypes.bool,
  shouldDisplayBlurCountdown: PropTypes.bool,
  toyCommandHintOpenedTimestamp: PropTypes.number,
  shouldUseLivestreamAlert: PropTypes.bool,
  privateShowStatus: PropTypes.string,
  isKaraokeDeviceOnline: PropTypes.bool,
  login: PropTypes.func,
  setChatPanel: PropTypes.func,
  setGamePanel: PropTypes.func,
  setTriggerPrivatePanel: PropTypes.func,
  setGiftPanel: PropTypes.func,
  openModal: PropTypes.func,
  toggleModal: PropTypes.func,
  checkLivestreamChatPreflightAndOpenPanel: PropTypes.func,
  setToyCommandHintOpenedTimestamp: PropTypes.func,
  t: PropTypes.func.isRequired,
};

LivestreamDetailOnlineFooter.defaultProps = {
  streamId: null,
  streamStatus: null,
  streamPreviewBlurDuration: 30,
  isAuthed: false,
  isStreamActivated: false,
  isLiveStreaming: null,
  isStreamer: false,
  isChatPanelOpen: false,
  isGamePanelOpen: false,
  isTriggerPrivatePanelOpen: false,
  isSuperChatOpen: false,
  hasStreamToken: false,
  hasGames: false,
  shouldDisplayBlurCountdown: false,
  toyCommandHintOpenedTimestamp: 0,
  shouldUseLivestreamAlert: false,
  privateShowStatus: null,
  isKaraokeDeviceOnline: false,
  login: NULL_FUNCTION,
  setChatPanel: NULL_FUNCTION,
  setGamePanel: NULL_FUNCTION,
  setTriggerPrivatePanel: NULL_FUNCTION,
  setGiftPanel: NULL_FUNCTION,
  openModal: NULL_FUNCTION,
  toggleModal: NULL_FUNCTION,
  checkLivestreamChatPreflightAndOpenPanel: NULL_FUNCTION,
  setToyCommandHintOpenedTimestamp: NULL_FUNCTION,
};

const StyledLivestreamDetailOnlineFooter = styled.div`
  z-index: 2; //ensure footer has higher z-index than LivstreamAside
  display: none;

  ${media.tablet`
    display: block;
  `}
`;

const ControlPanel = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
  padding: 0px 8px
    max(16px, ${isIphoneXSeriesPWA ? iPhoneXSeriesFooterPaddingInPWA : 0}px);
`;

const MobileAndTabletControlButton = styled.button`
  ${ResetButtonStyle};
  max-width: 40px;
  max-height: 40px;
  min-width: 40px;
  min-height: 40px;
  display: none;
  padding: 6px;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  background: rgba(25, 25, 25, 0.2);

  > img {
    object-fit: contain;
  }
  :last-child {
    margin-bottom: 0px;
  }
  ${media.tablet`
    display: inline-flex;
  `};
`;

const MobileAndTabletGiftButton = styled(MobileAndTabletControlButton)`
  position: relative;
  > img {
    width: 100%;
    height: 100%;
  }
`;

const MobileAndTabletGameButton = styled(MobileAndTabletControlButton)`
  ${({ $isActive }) =>
    $isActive
      ? css`
          background-color: ${color.neutral['20_alpha_40']};
        `
      : css``}
  > img {
    width: 100%;
    height: 100%;
  }
`;

const MobileAndTabletPrivateCtaButton = styled(MobileAndTabletControlButton)`
  ${({ $isActive }) =>
    $isActive
      ? css`
          background-color: ${color.neutral['20_alpha_40']};
        `
      : css``}
  > img {
    width: 100%;
    height: 100%;
  }
`;

const MobileAndTabletToyCommandButton = styled(MobileAndTabletControlButton)`
  position: relative;
`;

const ToyCommandButtonWrapper = styled.div`
  display: none;
  position: relative;
  align-items: center;
  justify-content: center;

  ${props =>
    props.$shouldHide &&
    css`
      display: none;
    `}

  ${media.tablet`
    display: inline-flex;
    ${props =>
      props.$shouldHide &&
      css`
        display: none;
      `}
  `};
`;

const ChatInputDisplay = styled.div`
  flex: 1;
  min-width: 0;
  max-height: 40px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 12px;
  height: 40px;
  border-radius: 20px;
  border: solid 1px ${color.neutral['30']};
  background-color: rgba(25, 25, 25, 0.2);

  visibility: hidden;
  pointer-events: none;

  ${media.tablet`
    cursor: ${({ $shouldDisplay }) => ($shouldDisplay ? 'pointer' : 'default')};
    visibility: ${({ $isHidden, $shouldDisplay }) =>
      $isHidden || !$shouldDisplay ? 'hidden' : 'visible'};
    pointer-events: ${({ $isHidden, $shouldDisplay }) =>
      $isHidden || !$shouldDisplay ? 'none' : 'auto'};
    opacity: ${({ $shouldDisplay }) => ($shouldDisplay ? '1' : '0')};
  `}
`;

const ChatInputText = styled.div`
  flex: 1;
  color: ${color.neutral['100_alpha_40']};
  font-size: 14px;
  user-select: none;
`;

const ChatSendIcon = styled.div`
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;

  > img {
    width: 20px;
    height: 20px;
    object-fit: contain;
  }
`;

const ToyCommandTooltipWrapper = styled.div`
  position: relative;
  border-radius: 4px;
  padding: 6px 8px;
  margin-bottom: 12px;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.5;
  color: ${color.neutral[100]};
  background-color: ${color.neutral[0]};
  text-align: center;
  display: inline-block;
  width: max-content;
  max-width: 250px;
  white-space: normal;
  word-wrap: break-word;
  word-break: break-word;
  box-shadow: 0px 2px 8px 0px rgba(25, 25, 25, 0.2);

  &::after {
    position: absolute;
    bottom: -4px;
    right: 20px;
    width: 8px;
    height: 8px;
    background-color: ${color.neutral[0]};
    transform: rotate(45deg);
    content: '';
  }
`;

const MobileAndTabletChatPanelContainer = styled.div`
  display: none;
  ${media.tablet`
    display: block;
  `};
`;

const ChatBackDrop = styled.div`
  position: absolute;
  top: ${headerHeight}px;
  bottom: 0px;
  left: 0px;
  right: 0px;
`;

const ChatPanel = styled.div`
  position: relative;
  padding: 8px 16px
    ${isIphoneXSeriesPWA ? iPhoneXSeriesFooterPaddingInPWA : 8}px;
  transition: max-height 0.4s ease-out;
  z-index: 1;
  background-color: ${color.neutral[10]};
`;

const ChatPanelInputWrapper = styled.div``;

const SuperChatPanel = styled.div`
  position: relative;
  z-index: 1;
`;

const ToyCommandIconContainer = styled.div`
  position: relative;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  will-change: transform;
  transform: translateZ(0);
  z-index: 2;

  > img {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  > img:first-child {
    /* ToyCommand icon*/
    opacity: 1;
    transform: translate(-50%, -50%);
    will-change: transform, opacity;
  }
`;

export default withTranslation()(LivestreamDetailOnlineFooter);
